import React, { useState, useEffect, useCallback } from 'react';
import {
    X, Bell, Eye, CheckCircle2, AlertTriangle,
    BarChart3, RefreshCw, Layers, Download
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
    StatCard,
    BarChart,
    DataTable,
    LineChart,
    AIInsightCard,
    ExpandableVisualization
} from '../Analytics';
import useDrawerResize from './useDrawerResize';
import AnalyticsAIPanel from './AnalyticsAIPanel';
import NotificationSettingsPanel from './NotificationSettingsPanel';

const NoticesAnalyticsDrawer = ({ isOpen, onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    // ── Resizable drawer ──
    const { drawerWidth, isDragging, dragHandleProps } = useDrawerResize({
        minWidth: 400, maxWidth: 800, defaultWidth: 600,
        storageKey: 'noticesAnalyticsDrawerWidth',
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await api.get('/analytics/notices', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setData(response.data.analytics);
        } catch (error) {
            console.error('Error fetching notice analytics:', error);
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleDownloadCSV = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.post('/analytics/report', {
                reportType: 'notices', format: 'csv'
            }, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = 'notice-analytics.csv';
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success('Notice analytics exported');
        } catch { toast.error('Export failed'); }
    };

    useEffect(() => {
        if (isOpen) {
            fetchData();
            document.body.style.overflow = 'hidden';
        } else {
            setData(null);
            document.body.style.overflow = 'unset';
            setLoading(true);
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, fetchData]);

    // Close on Escape
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div
                className="absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />
            <div
                className="relative h-full bg-[#FAFAF9] shadow-2xl flex flex-col animate-slide-in-right"
                style={{ width: `${drawerWidth}px`, maxWidth: '100vw', transition: isDragging ? 'none' : undefined }}
            >
                {/* ── Drag handle (left edge) ── */}
                <div {...dragHandleProps} />
                {/* Header */}
                <div className="px-6 py-4 bg-white border-b border-[#E5E1DD] flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-50 rounded-lg">
                            <Bell className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Notice Analytics</h2>
                            <p className="text-sm text-gray-500">Reach and engagement metrics</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleDownloadCSV}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Download CSV"
                        >
                            <Download className="w-5 h-5" />
                        </button>
                        <button
                            onClick={fetchData}
                            disabled={loading}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 scrollbar-hide">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-64 space-y-4">
                            <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
                            <p className="text-gray-500 font-medium">Loading analytics...</p>
                        </div>
                    ) : data ? (
                        <div className="space-y-6">
                            {/* Summary Cards */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <StatCard
                                    title="Active Notices"
                                    value={data.activeNotices || 0}
                                    icon={Bell}
                                    color="blue"
                                />
                                <StatCard
                                    title="Total Views"
                                    value={data.totalViews || 0}
                                    icon={Eye}
                                    color="indigo"
                                />
                                <StatCard
                                    title="Engagement Rate"
                                    value={`${data.engagementRate || 0}%`}
                                    icon={CheckCircle2}
                                    color={data.engagementRate >= 50 ? 'green' : 'amber'}
                                />
                                <StatCard
                                    title="Critical Notices"
                                    value={data.criticalNotices || 0}
                                    icon={AlertTriangle}
                                    color="red"
                                />
                            </div>

                            {/* Views Overview */}
                            {data.viewsAssessment && (
                                <ExpandableVisualization
                                    title="Engagement by Priority"
                                    data={[
                                        { label: 'High Priority', value: data.viewsAssessment.high || 0 },
                                        { label: 'Medium Priority', value: data.viewsAssessment.medium || 0 },
                                        { label: 'Low Priority', value: data.viewsAssessment.low || 0 }
                                    ]}
                                    columns={[{ key: 'label', label: 'Priority' }, { key: 'value', label: 'Views' }]}
                                    exportFilename="notice-priority-engagement"
                                >
                                    <div className="bg-white rounded-xl border border-[#E5E1DD] p-6 shadow-sm">
                                        <h3 className="font-semibold text-gray-800 mb-4">Engagement by Priority</h3>
                                        <BarChart
                                            data={[
                                                { label: 'High Priority', value: data.viewsAssessment.high || 0, color: '#ef4444' },
                                                { label: 'Medium Priority', value: data.viewsAssessment.medium || 0, color: '#f59e0b' },
                                                { label: 'Low Priority', value: data.viewsAssessment.low || 0, color: '#10b981' }
                                            ]}
                                            horizontal
                                            showValues
                                        />
                                    </div>
                                </ExpandableVisualization>
                            )}

                            {/* Top Viewed Notices */}
                            {data.topNotices && data.topNotices.length > 0 && (
                                <ExpandableVisualization
                                    title="Top Viewed Notices"
                                    data={data.topNotices}
                                    columns={[{ key: 'title', label: 'Notice' }, { key: 'priority', label: 'Priority' }, { key: 'views', label: 'Views' }, { key: 'acknowledgementRate', label: 'Ack Rate %' }]}
                                    exportFilename="top-notices"
                                >
                                    <div className="bg-white rounded-xl border border-[#E5E1DD] overflow-hidden shadow-sm">
                                        <div className="p-4 border-b border-[#E5E1DD]">
                                            <h3 className="font-semibold text-gray-800">Top Viewed Notices</h3>
                                        </div>
                                        <DataTable
                                            data={data.topNotices}
                                            columns={[
                                                { key: 'title', label: 'Notice', render: (val) => <span className="font-medium text-gray-900">{val}</span> },
                                                {
                                                    key: 'priority', label: 'Priority', render: (val) => (
                                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${val === 'high' ? 'bg-red-100 text-red-700' :
                                                            val === 'medium' ? 'bg-amber-100 text-amber-700' :
                                                                'bg-blue-100 text-blue-700'
                                                            }`}>
                                                            {val?.toUpperCase()}
                                                        </span>
                                                    )
                                                },
                                                { key: 'views', label: 'Views' },
                                                {
                                                    key: 'acknowledgementRate',
                                                    label: 'Ack. Rate',
                                                    render: (val) => (
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
                                                                <div
                                                                    className={`h-full rounded-full ${val >= 70 ? 'bg-green-500' : val >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                                                                    style={{ width: `${val}%` }}
                                                                />
                                                            </div>
                                                            <span className="text-xs">{val}%</span>
                                                        </div>
                                                    )
                                                }
                                            ]}
                                            searchable
                                            searchKeys={['title']}
                                            pageSize={10}
                                        />
                                    </div>
                                </ExpandableVisualization>
                            )}

                            {/* Enhanced Notice Metrics */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="bg-white rounded-xl border border-gray-200 p-5">
                                    <p className="text-sm font-medium text-gray-500 mb-1">Avg Time to View</p>
                                    <h3 className="text-2xl font-bold text-gray-900">{data.avgTimeToViewHours || '0'}h</h3>
                                    <p className="text-xs text-gray-400 mt-1">From publish to first view</p>
                                </div>
                                <div className="bg-white rounded-xl border border-gray-200 p-5">
                                    <p className="text-sm font-medium text-gray-500 mb-1">Popup Dismiss Rate</p>
                                    <h3 className="text-2xl font-bold text-gray-900">{data.popupDismissRate || 0}%</h3>
                                    <p className="text-xs text-gray-400 mt-1">{data.popupDismissed || 0} of {data.totalViews || 0} dismissed</p>
                                </div>
                                <div className="bg-white rounded-xl border border-gray-200 p-5">
                                    <p className="text-sm font-medium text-gray-500 mb-1">Published</p>
                                    <h3 className="text-2xl font-bold text-gray-900">{data.publishedNotices || 0}</h3>
                                    <p className="text-xs text-gray-400 mt-1">{data.draftNotices || 0} drafts</p>
                                </div>
                            </div>

                            {/* Engagement by Priority */}
                            {data.byPriority && data.byPriority.length > 0 && (
                                <ExpandableVisualization
                                    title="Engagement by Priority Level"
                                    data={data.byPriority}
                                    columns={[{ key: 'priority', label: 'Priority' }, { key: 'totalViews', label: 'Views' }, { key: 'noticeCount', label: 'Notices' }]}
                                    exportFilename="notice-priority-breakdown"
                                >
                                    <div className="bg-white rounded-xl border border-[#E5E1DD] p-6 shadow-sm">
                                        <h3 className="font-semibold text-gray-800 mb-4">Engagement by Priority</h3>
                                        <BarChart
                                            data={data.byPriority.map(d => ({
                                                label: (d.priority || 'normal').charAt(0).toUpperCase() + (d.priority || 'normal').slice(1),
                                                value: d.totalViews,
                                                color: d.priority === 'high' || d.priority === 'urgent' ? '#ef4444' : d.priority === 'normal' ? '#f59e0b' : '#10b981'
                                            }))}
                                            horizontal
                                            showValues
                                        />
                                    </div>
                                </ExpandableVisualization>
                            )}

                            {/* Views Over Time */}
                            {data.viewsHistory && data.viewsHistory.length > 0 && (
                                <ExpandableVisualization
                                    title="Views (Last 30 Days)"
                                    data={data.viewsHistory}
                                    columns={[{ key: 'date', label: 'Date' }, { key: 'views', label: 'Views' }]}
                                    exportFilename="notice-views-trend"
                                >
                                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                                        <h3 className="font-semibold text-gray-800 mb-4">Views (Last 30 Days)</h3>
                                        <LineChart
                                            data={data.viewsHistory.map(d => ({
                                                date: d.date,
                                                value: d.views
                                            }))}
                                            height={200}
                                            lineColor="#6366f1"
                                        />
                                    </div>
                                </ExpandableVisualization>
                            )}


                            {/* AI Insights */}
                            <AIInsightCard
                                title="Notice Insights"
                                insight={`You have ${data.activeNotices || 0} active notices. Engagement rate is ${data.engagementRate || 0}%. ${data.criticalNotices > 0 ? `Attention: ${data.criticalNotices} critical notices are active.` : 'Notice board is updated.'}`}
                                suggestions={[
                                    "Which notices have the least views?",
                                    "Show me critical notices not yet acknowledged",
                                    "Best time to post notices for max engagement",
                                    "List employees who haven't viewed the latest policy"
                                ]}
                                context={{ notices: data, type: 'notices' }}
                            />

                            {/* AI Chat Panel */}
                            <AnalyticsAIPanel
                                analyticsContext={{
                                    type: 'notices',
                                    title: 'Notice Analytics',
                                    data: data,
                                }}
                            />

                            {/* Notification Settings */}
                            <NotificationSettingsPanel
                                analyticsContext={{
                                    type: 'notices',
                                    title: 'Notice Analytics',
                                    data: data,
                                }}
                            />
                        </div>
                    ) : (
                        <div className="text-center py-12 text-gray-500">
                            <div className="bg-gray-50 rounded-lg p-6 max-w-sm mx-auto border border-gray-200">
                                <BarChart3 className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                                <h3 className="text-gray-900 font-medium mb-1">No Analytics Available</h3>
                                <p className="text-sm text-gray-500">
                                    We couldn't load notice analytics at this time.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NoticesAnalyticsDrawer;
